import type {FamilyMember, Category} from '@/types';
import styles from './Sidebar.module.css';

interface SidebarProps {
  members: FamilyMember[];
  categories: Category[];
  activeMemberId: string;
  activeCategoryName: string | null;
  onMemberSelect: (memberId: string) => void;
  onCategorySelect: (categoryName: string) => void;
  onAddMember: () => void;
  onShare: () => void;
  onLogout: () => void;
}

export function Sidebar({
  members,
  categories,
  activeMemberId,
  activeCategoryName,
  onMemberSelect,
  onCategorySelect,
  onAddMember,
  onShare,
  onLogout,
}: SidebarProps) {
  return (
    <aside className={styles.sidebar}>
      <div className={styles.logoWrap}>
        <span className={styles.logo}>Closai</span>
      </div>

      <div className={styles.section}>
        <div className={styles.label}>Family</div>
        {members.map(member => (
          <button
            key={member.id}
            className={[styles.item, activeMemberId === member.id ? styles.itemActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => onMemberSelect(member.id)}
          >
            <span
              className={styles.avatar}
              style={{background: member.color}}
            >
              {member.icon}
            </span>
            <span className={styles.itemText}>{member.name}</span>
          </button>
        ))}
        <button className={styles.addBtn} onClick={onAddMember}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span className={styles.addText}>Add member</span>
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.section}>
        <div className={styles.label}>Categories</div>
        {categories.map(cat => (
          <button
            key={cat.name}
            className={[
              styles.item,
              activeCategoryName === cat.name ? styles.itemActive : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onCategorySelect(cat.name)}
          >
            <span className={styles.catIcon}>{cat.icon}</span>
            <span className={styles.itemText}>{cat.name}</span>
            <span className={styles.count}>{cat.count}</span>
          </button>
        ))}
      </div>

      <div className={styles.footer}>
        <button className={styles.footerBtn} onClick={onShare}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          <span className={styles.footerText}>Share</span>
        </button>
        <button className={[styles.footerBtn, styles.logoutBtn].join(' ')} onClick={onLogout}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          <span className={styles.footerText}>Log out</span>
        </button>
      </div>
    </aside>
  );
}
